/**
 * The Team reads (t-75b4cb9e, contracts in packages/contracts/src/team.ts, routes.team.ts): Daily update / Done today,
 * Created today, Appraisal record / My record, Workload. A member's calls are scoped to themselves by the server (any
 * other user_id is 403). The appraisal-record writes (t-31fbce66): log an incident, decide the cause, dismiss, add
 * my side — each returns the RecordEntry and refreshes every record read plus the menu badge (Not reviewed).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateIncidentBody,
  DecideIncidentCauseBody,
  DismissIncidentBody,
  IncidentResponseBody,
  RecordEntry,
  RecordPeriod,
  RouteQuery,
  TeamDailyQuery,
} from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../api';
import { ME_KEY } from './session';

export const teamKeys = {
  daily: (q: TeamDailyQuery) => ['team', 'daily', q] as const,
  createdToday: ['team', 'created-today'] as const,
  record: (q: { user_id?: string; period: RecordPeriod; dismissed?: boolean }) => ['team', 'record', q] as const,
  workload: ['team', 'workload'] as const,
};

export function useTeamDaily(q: TeamDailyQuery) {
  return useQuery({ queryKey: teamKeys.daily(q), queryFn: () => call('team.daily', { query: q as RouteQuery<'team.daily'> }) });
}

export function useCreatedToday() {
  return useQuery({ queryKey: teamKeys.createdToday, queryFn: () => call('team.createdToday') });
}

/** The kind filter runs on the loaded entries (the counts ignore it anyway), so switching kinds needs no reload. */
export function useTeamRecord(q: { user_id?: string; period: RecordPeriod; dismissed?: boolean }) {
  return useQuery({
    queryKey: teamKeys.record(q),
    queryFn: () =>
      call('team.record', {
        query: { period: q.period, ...(q.user_id ? { user_id: q.user_id } : {}), ...(q.dismissed ? { dismissed: 'true' } : {}) } as RouteQuery<'team.record'>,
      }),
    placeholderData: (prev) => prev,
  });
}

/**
 * GET /incidents/{id} — one appraisal-record entry by id: the deep link ?incident=<id> (Review queue "Decide the
 * cause", Work, Today). Managers get any entry, including one with no owner (owner null, relation caused) that is on
 * nobody's record; a member only an entry about themselves. Anything else is 404 (an id is never confirmed), a bad id
 * 422. Keyed under ['team', 'record'] so every record write refreshes it.
 */
export function useRecordEntry(id: string | null) {
  return useQuery({
    queryKey: ['team', 'record', 'entry', id] as const,
    queryFn: () => call('incidents.get', { params: { id: id! } }),
    enabled: !!id,
    retry: false,
  });
}

/** Every record read (each person, period and the dismissed list) and the menu badge go stale after a write. */
function useRecordWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['team', 'record'] }), qc.invalidateQueries({ queryKey: ME_KEY })]);
  return useMutation({ mutationFn: fn, onSuccess: refresh, onError: refresh });
}

/** POST /incidents — "+ Log an incident" (managers). */
export function useCreateIncident() {
  return useRecordWrite((body: CreateIncidentBody) => call('incidents.create', { body, idempotencyKey: newIdempotencyKey() }));
}

/** PATCH /incidents/{id}/cause (If-Match = entry.version, required) — "Review and decide the cause" / "Change the cause". */
export function useDecideIncidentCause() {
  return useRecordWrite((v: { id: string; version: number; body: DecideIncidentCauseBody }) =>
    call('incidents.decideCause', { params: { id: v.id }, body: v.body, ifMatch: v.version, idempotencyKey: newIdempotencyKey() }),
  );
}

/** POST /incidents/{id}/dismiss — kept, marked dismissed with the reason, never counted. */
export function useDismissIncident() {
  return useRecordWrite((v: { id: string; version: number; body: DismissIncidentBody }) =>
    call('incidents.dismiss', { params: { id: v.id }, body: v.body, ifMatch: v.version, idempotencyKey: newIdempotencyKey() }),
  );
}

/** POST /incidents/{id}/response — "Add my side" / "Edit my side" (the owner or the person it was attributed to). */
export function useRespondToIncident() {
  return useRecordWrite((v: { id: string; version: number; body: IncidentResponseBody }) =>
    call('incidents.respond', { params: { id: v.id }, body: v.body, ifMatch: v.version, idempotencyKey: newIdempotencyKey() }),
  );
}

export function useWorkload() {
  return useQuery({ queryKey: teamKeys.workload, queryFn: () => call('team.workload') });
}
